import React from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import { useNavigate } from "react-router-dom";

import {
  Eye, Music, MessageSquare, Settings, MousePointer,
  HelpCircle, BookOpen, Users
} from "lucide-react";
import { cn } from "@/lib/utils";

interface GuideSectionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  items: string[];
}

function GuideSection({ icon, title, description, items }: GuideSectionProps) {
  return (
    <div
      className="bg-card rounded-2xl p-6 border border-border transition-all"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
          {icon}
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={index} className="flex items-start gap-2 text-foreground/80">
            <span className="mt-1 text-primary flex-shrink-0 text-base leading-none">•</span>
            <span>
              {item.split("\n").map((line, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Guide() {
  const navigate = useNavigate();
  const { t, isRTL } = useCaregiverTranslation();

  return (
    <MainLayout>
      <div className="flex-1 flex flex-col min-h-0 bg-background" dir={isRTL ? "rtl" : "ltr"}>
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b-2 border-primary/40 bg-card/50">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            <div className={isRTL ? "text-right" : "text-left"}>
              <h1 className="text-3xl font-bold text-foreground">{t("guideTitle")}</h1>
              <p className="text-lg text-muted-foreground mt-1">{t("guideIntro")}</p>
            </div>
          </div>
          <p className={cn("mt-4 text-foreground/80 max-w-3xl", isRTL ? "text-right" : "text-left")}>{t("guideIntroDesc")}</p>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-6 max-w-4xl mx-auto space-y-6">

            {/* Control Methods — only enabled methods */}
            <GuideSection
              icon={<MousePointer className="w-6 h-6" />}
              title={t("guideControlMethods")}
              description={t("guideControlMethodsDesc")}
              items={[
                t("guideControlEyeControl"),
                t("guideControlSwitch"),
                t("guideControlTouch"),
              ]}
            />

            {/* Communication — AI Chat removed */}
            <GuideSection
              icon={<MessageSquare className="w-6 h-6" />}
              title={t("guideCommunication")}
              description={t("guideCommunicationDesc")}
              items={[
                t("guideCommBoard"),
                t("guideCommAAC"),
                t("guideCommHelp"),
                t("guideCommTranslator"),
              ]}
            />

            {/* Contacts */}
            <GuideSection
              icon={<Users className="w-6 h-6" />}
              title={t("guideContacts")}
              description={t("guideContactsDesc")}
              items={[
                t("guideContactsItem0"),
                t("guideContactsItem1"),
                t("guideContactsItem2"),
                t("guideContactsItem3"),
                t("guideContactsItem4"),
              ]}
            />

            {/* Media & Entertainment — TV and Games removed */}
            <GuideSection
              icon={<Music className="w-6 h-6" />}
              title={t("guideMedia")}
              description={t("guideMediaDesc")}
              items={[
                t("guideMediaYT"),
                t("guideMediaRadio"),
                t("guideMediaSpotify"),
              ]}
            />

            {/* Settings */}
            <GuideSection
              icon={<Settings className="w-6 h-6" />}
              title={t("guideSettings")}
              description={t("guideSettingsDesc")}
              items={[
                t("guideSettingsAppearance"),
                t("guideSettingsVoice"),
                t("guideSettingsProfile"),
                t("guideSettingsControl"),
                t("guideSettingsBoards"),
              ]}
            />

            {/* Accessibility */}
            <GuideSection
              icon={<Eye className="w-6 h-6" />}
              title={t("guideAccessibility")}
              description={t("guideAccessibilityDesc")}
              items={[
                t("guideAccessScanning"),
                t("guideAccessHelp"),
                t("guideAccessRest"),
                t("guideAccessDwell"),
              ]}
            />

            {/* Tips */}
            <GuideSection
              icon={<HelpCircle className="w-6 h-6" />}
              title={t("guideTips")}
              description={t("guideTipsDesc")}
              items={[
                t("guideTip1"),
                t("guideTip2"),
                t("guideTip3"),
                t("guideTip4"),
                t("guideTip5"),
              ]}
            />

            {/* Version info */}
            <div className="text-center py-8 text-muted-foreground">
              <p>EyeControl App AAC System v1.0</p>
              <p className="text-sm mt-1">© 2025 EyeControl</p>
            </div>
          </div>
        </div>

      </div>
    </MainLayout>
  );
}
